import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Spinner from './Spinner';
import styles from './AdminPage.module.css';
import arrowLeft from '../assets/arrow-left.svg';
import arrowRight from '../assets/arrow-right.svg';

const ITEMS_PER_PAGE = 30;

const AdminPage = ({ darkMode, authToken }) => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/logs`, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
          },
          credentials: 'include',
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Ошибка при загрузке логов');
        }

        const data = await response.json();
        // Агрегация данных по пользователям
        const aggregatedData = data.logs.reduce((acc, log) => {
          const { email, name, timestamp } = log;
          if (!acc[email]) {
            acc[email] = {
              email,
              name: name || 'Не указано',
              loginCount: 0,
              lastLogin: null,
            };
          }
          if (log.event === 'login') {
            acc[email].loginCount += 1;
            // Обновляем lastLogin, если текущий timestamp новее
            if (!acc[email].lastLogin || new Date(timestamp) > new Date(acc[email].lastLogin)) {
              acc[email].lastLogin = timestamp;
            }
          }
          return acc;
        }, {});

        // Преобразование объекта в массив и сортировка по email
        const sortedUserData = Object.values(aggregatedData).sort((a, b) =>
          a.email.localeCompare(b.email)
        );
        setUserData(sortedUserData);
      } catch (err) {
        setError(err.message);
        if (err.message.includes('Токен отсутствует') || err.message.includes('Неверный токен')) {
          navigate('/auth');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [authToken, navigate]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getPageNumbers = () => {
    const totalItems = userData.length;
    const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
    const maxPagesToShow = window.innerWidth <= 375 ? 3 : window.innerWidth <= 768 ? 5 : 7;
    const pages = [];

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      const sidePages = Math.floor((maxPagesToShow - 3) / 2);
      let startPage = Math.max(2, currentPage - sidePages);
      let endPage = Math.min(totalPages - 1, currentPage + sidePages);

      if (endPage - startPage + 2 < maxPagesToShow - 2) {
        if (currentPage < totalPages / 2) {
          endPage = Math.min(totalPages - 1, startPage + maxPagesToShow - 3);
        } else {
          startPage = Math.max(2, endPage - maxPagesToShow + 3);
        }
      }

      if (startPage > 2) {
        pages.push('...');
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      if (endPage < totalPages - 1) {
        pages.push('...');
      }

      pages.push(totalPages);
    }

    return pages;
  };

  const totalPages = Math.ceil(userData.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentUsers = userData.slice(startIndex, endIndex);

  return (
    <div className={styles.adminContainer}>
      <h1 className={styles.mainTitle}>Логи пользователей</h1>
      {error && <div className={styles.errorMessage}>{error}</div>}
      {loading ? (
        <div className={styles.spinnerContainer}>
          <Spinner darkMode={darkMode} />
        </div>
      ) : userData.length === 0 ? (
        <div className={styles.noLogs}>Логи отсутствуют</div>
      ) : (
        <>
          <div className={styles.logsList}>
            <div className={styles.tableWrapper}>
              <table className={styles.userTable}>
                <thead>
                  <tr>
                    <th>№</th>
                    <th>Email</th>
                    <th>Имя</th>
                    <th>Количество входов</th>
                    <th>Последний вход</th>
                  </tr>
                </thead>
                <tbody>
                  {currentUsers.map((user, index) => (
                    <tr key={user.email}>
                      <td>{startIndex + index + 1}</td>
                      <td>{user.email}</td>
                      <td>{user.name}</td>
                      <td>{user.loginCount}</td>
                      <td>
                        {user.lastLogin
                          ? new Date(user.lastLogin).toLocaleString('ru-RU')
                          : 'Нет данных'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {totalPages > 1 && (
            <div className={styles.paginationContainer}>
              <button
                className={styles.paginationButton}
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                aria-label="Предыдущая страница"
              >
                <img
                  src={arrowLeft}
                  alt="Предыдущая страница"
                  className={styles.paginationIcon}
                />
              </button>
              <div className={styles.pageNumbers}>
                {getPageNumbers().map((page, index) =>
                  page === '...' ? (
                    <span key={`ellipsis-${index}`} className={styles.ellipsis}>
                      ...
                    </span>
                  ) : (
                    <button
                      key={page}
                      className={`${styles.paginationPageButton} ${currentPage === page ? styles.activePage : ''}`}
                      onClick={() => handlePageChange(page)}
                      aria-label={`Страница ${page}`}
                      aria-current={currentPage === page ? 'page' : undefined}
                    >
                      {page}
                    </button>
                  )
                )}
              </div>
              <button
                className={styles.paginationButton}
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                aria-label="Следующая страница"
              >
                <img
                  src={arrowRight}
                  alt="Следующая страница"
                  className={styles.paginationIcon}
                />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminPage;